import { z } from "zod";
import { planSourceQuantitiesSchema } from "./production/plan-source-quantities.js";

export const generalProductionUnitSchema = z.enum(["kg", "koli", "adet"]);
export const generalProductionShiftSchema = z.enum(["VR1", "VR2", "VR3"]);
export const generalProductionIssueCodeSchema = z.enum([
  "MISSING_WORK_ORDER", "UNIT_UNKNOWN", "UNIT_MISMATCH", "LINE_UNRESOLVED",
  "LINE_MISMATCH", "QUERY_TRUNCATED", "UNPLANNED_ACTUAL",
  "SOURCE_VALIDATION",
]);

export const generalProductionPlanSchema = z.object({
  id: z.string().min(1), weekStart: z.iso.date(), weekEnd: z.iso.date(),
  version: z.number().int().positive(), status: z.enum(["ACTIVE", "SUPERSEDED", "SOURCE"]),
  sheetName: z.string().min(1), origin: z.enum(["EXCEL", "SYSTEM"]),
}).strict();

export const generalProductionLineSchema = z.object({
  key: z.string().min(1), id: z.string().nullable(), code: z.string().nullable(), name: z.string().min(1),
  departmentName: z.string().nullable(), active: z.boolean(), hasPlan: z.boolean(),
}).strict();

export const generalProductionActualEntrySchema = z.object({
  date: z.iso.date(), shift: generalProductionShiftSchema.nullable(), quantity: z.number().nonnegative(),
}).strict();
export const generalProductionActualClassificationSchema = z.enum(["PLAN_MATCHED", "UNPLANNED", "REVIEW"]);

export const generalProductionRowSchema = z.object({
  id: z.string().min(1), kind: z.enum(["PLAN", "UNMATCHED_ACTUAL"]), sourceRow: z.number().int().positive().nullable(),
  date: z.iso.date(), lineKey: z.string().nullable(), lineName: z.string().nullable(),
  productCode: z.string().min(1), productName: z.string().min(1), workOrderNo: z.string().nullable(),
  unit: generalProductionUnitSchema.nullable(), targetQuantity: z.number().nonnegative(),
  actualQuantity: z.number().nonnegative().nullable(), remainingQuantity: z.number().nonnegative().nullable(),
  actualState: z.enum(["OBSERVED", "FUTURE", "UNRESOLVED"]),
  actualClassification: generalProductionActualClassificationSchema,
  actualDocumentNos: z.array(z.string().min(1)),
  sourceQuantities: planSourceQuantitiesSchema.optional(),
  actualEntries: z.array(generalProductionActualEntrySchema), actualDates: z.array(z.iso.date()),
  actualShiftCodes: z.array(generalProductionShiftSchema), issueCodes: z.array(generalProductionIssueCodeSchema),
  issueMessages: z.array(z.string().min(1)),
}).strict();

export const generalProductionUnitSummarySchema = z.object({
  unit: generalProductionUnitSchema, targetQuantity: z.number().nonnegative(),
  matchedActualQuantity: z.number().nonnegative().nullable(), unmatchedActualQuantity: z.number().nonnegative(),
  totalActualQuantity: z.number().nonnegative(), verifiedMatchedActualQuantity: z.number().nonnegative(),
  unplannedActualQuantity: z.number().nonnegative(), reviewActualQuantity: z.number().nonnegative(),
  remainingQuantity: z.number().nonnegative().nullable(), unresolvedPlanRowCount: z.number().int().nonnegative(),
}).strict();

export const generalProductionResponseSchema = z.object({
  plans: z.array(generalProductionPlanSchema), selectedPlan: generalProductionPlanSchema.nullable(),
  lines: z.array(generalProductionLineSchema),
  range: z.object({ planFrom: z.iso.date().nullable(), planTo: z.iso.date().nullable(), actualTo: z.iso.date().nullable() }).strict(),
  selectedShift: generalProductionShiftSchema.nullable(), observedAt: z.string().nullable(), actualsComplete: z.boolean(),
  historicalPackagingFrozenAt:z.string().optional(),
  rows: z.array(generalProductionRowSchema), summary: z.array(generalProductionUnitSummarySchema),
  reportSource: z.object({
    fileName: z.string(), fileHash: z.string(), rowCount: z.number().int().nonnegative(),
    unresolvedRows: z.array(z.object({
      sourceRow: z.number().int().positive(), lineName: z.string(), productCode: z.string(), productName: z.string(),
      workOrderNo: z.string().nullable(), quantity: z.number().nullable(), unit: generalProductionUnitSchema.nullable(),
      reasons: z.array(z.string()),
      hasDailyAllocation:z.boolean(),
    })),
  }).optional(),
  carryover: z.object({
    previousWeekStart: z.iso.date(), frozenAt: z.string().nullable(),
    observedAt: z.string().nullable(),
    rows: z.array(z.object({
      id: z.string(), lineName: z.string(), lineKey: z.string(), workOrderNo: z.string().nullable(),
      productCode: z.string(), productName: z.string(), unit: generalProductionUnitSchema.nullable(),
      openingQuantity: z.number().nonnegative().nullable(), actualQuantity: z.number().nonnegative().nullable(),
      remainingQuantity: z.number().nonnegative().nullable(),
      status: z.enum(["CONFIRMED", "IN_CURRENT_PLAN", "UNRESOLVED"]), reason: z.string().nullable(),
      evidence: z.enum(["FROZEN", "OBSERVED", "UNAVAILABLE"]),
    })),
  }).optional(),
}).strict();

export type GeneralProductionUnit = z.infer<typeof generalProductionUnitSchema>;
export type GeneralProductionShift = z.infer<typeof generalProductionShiftSchema>;
export type GeneralProductionIssueCode = z.infer<typeof generalProductionIssueCodeSchema>;
export type GeneralProductionPlan = z.infer<typeof generalProductionPlanSchema>;
export type GeneralProductionLine = z.infer<typeof generalProductionLineSchema>;
export type GeneralProductionRow = z.infer<typeof generalProductionRowSchema>;
export type GeneralProductionResponse = z.infer<typeof generalProductionResponseSchema>;
export type GeneralProductionCarryover = NonNullable<GeneralProductionResponse["carryover"]>;
